
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ShoppingCart, Gift, XCircle } from 'lucide-react';
import LoadingSpinner from '@/components/masterlicense/LoadingSpinner';

interface GiftCardProduct {
  id: string;
  name: string;
  description: string;
  image: {
    url: string;
  };
  countries: string[];
  currencies: {
    [currencyCode: string]: {
      min: number;
      max: number;
    };
  };
  denomination_type: string;
}

export interface TremendousProductsResponse {
  products: GiftCardProduct[];
}

const TremendousGiftCardSelection = () => {
  const [allProducts, setAllProducts] = useState<GiftCardProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedProductsDetails, setSelectedProductsDetails] = useState<GiftCardProduct[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { toast } = useToast();

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      // Get all products
      const { data: allProductsData, error: allProductsError } = await supabase.functions.invoke('tremendous', {
        body: {}
      });

      if (allProductsError) {
        throw new Error(allProductsError.message);
      }

      // Get selected products
      const { data: selectedProductsData, error: selectedProductsError } = await supabase.functions.invoke('tremendous', {
        body: { action: 'getSelectedProducts' }
      });

      if (selectedProductsError) {
        throw new Error(selectedProductsError.message);
      }
      
      if (allProductsData?.products && Array.isArray(allProductsData.products)) {
        setAllProducts(allProductsData.products);
      }
      
      if (selectedProductsData?.products && Array.isArray(selectedProductsData.products)) {
        setSelectedProducts(selectedProductsData.products);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      toast({
        title: "Error",
        description: "Failed to retrieve gift card data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleProductSelection = async (productId: string, selected: boolean) => {
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: { productId, selected, action: 'toggleProduct' }
      });

      if (error) {
        throw new Error(error.message);
      }
      
      if (data?.selectedProducts) {
        setSelectedProducts(data.selectedProducts);
        toast({
          title: "Gift Card Removed",
          description: "The gift card has been removed from rewards",
          variant: "default"
        });
      }
    } catch (error) {
      console.error("Error toggling product selection:", error);
      toast({
        title: "Error",
        description: "Failed to update gift card selection",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    // Filter products to get details for selected products
    if (allProducts.length > 0 && selectedProducts.length > 0) {
      const details = allProducts.filter(product => selectedProducts.includes(product.id));
      setSelectedProductsDetails(details);
    } else {
      setSelectedProductsDetails([]);
    }
  }, [allProducts, selectedProducts]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (selectedProductsDetails.length === 0) {
    return (
      <Card>
        <CardContent className="p-10 text-center">
          <Gift className="mx-auto h-10 w-10 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">No Gift Cards Selected</h3>
          <p className="text-sm text-muted-foreground mt-2">
            You haven't selected any gift cards to offer as rewards yet.
          </p>
          <Button variant="outline" className="mt-4" onClick={() => fetchProducts()}>
            Refresh
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Selected Gift Cards</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Card</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Currencies</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {selectedProductsDetails.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>
                    <div className="h-16 w-16 relative bg-muted rounded">
                      {product.image?.url ? (
                        <img 
                          src={product.image.url} 
                          alt={product.name} 
                          className="w-full h-full object-contain p-1" 
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://placehold.co/64x64?text=GC';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                          <ShoppingCart className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{product.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {product.denomination_type === 'FIXED' ? 'Fixed Amount' : 'Variable Amount'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {Object.keys(product.currencies).map(currency => (
                        <Badge key={currency} variant="secondary">
                          {currency}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => toggleProductSelection(product.id, false)}
                      title="Remove from rewards"
                    >
                      <XCircle className="h-5 w-5 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default TremendousGiftCardSelection;
